import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Tier {
  id: string;
  creatorName: string;
  creatorUrl: string;
  tierName: string;
  description: string;
  amount: number;
  currency: string;
  subscriberCount: number;
  isPublished: boolean;
  benefits: string[];
  createdAt: Date;
}

const AdminTiersPage: React.FC = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData: Tier[] = [
        {
          id: '1',
          creatorName: 'Art Studio',
          creatorUrl: 'artstudio',
          tierName: 'Basic Supporter',
          description: 'Get access to exclusive art content',
          amount: 5,
          currency: 'USD',
          subscriberCount: 245,
          isPublished: true,
          benefits: ['Early access to posts', 'Behind the scenes content'],
          createdAt: new Date('2024-01-15'),
        },
        {
          id: '2',
          creatorName: 'Art Studio',
          creatorUrl: 'artstudio',
          tierName: 'Premium Tier',
          description: 'Full access + exclusive perks',
          amount: 25,
          currency: 'USD',
          subscriberCount: 89,
          isPublished: true,
          benefits: ['All Basic benefits', 'Monthly video tutorials', 'Priority support'],
          createdAt: new Date('2024-02-01'),
        },
        {
          id: '3',
          creatorName: 'Music Producer',
          creatorUrl: 'musicpro',
          tierName: 'Fan Club',
          description: 'Support my music journey',
          amount: 10,
          currency: 'USD',
          subscriberCount: 156,
          isPublished: true,
          benefits: ['Exclusive tracks', 'Discord access'],
          createdAt: new Date('2024-03-10'),
        },
      ];
      setTiers(mockData);
    } catch (error) {
      console.error('Failed to load tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTiers = tiers.filter(
    (tier) =>
      tier.tierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tier.creatorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: tiers.length,
    published: tiers.filter((t) => t.isPublished).length,
    totalSubscribers: tiers.reduce((sum, t) => sum + t.subscriberCount, 0),
    avgPrice: tiers.reduce((sum, t) => sum + t.amount, 0) / tiers.length,
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">Loading tiers...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Membership Tiers</h1>
          <p className="mt-2 text-gray-600">Manage all creator membership tiers</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Tiers</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Published</p>
            <p className="text-3xl font-bold text-green-600">{stats.published}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Subscribers</p>
            <p className="text-3xl font-bold text-blue-600">{stats.totalSubscribers}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Avg Price</p>
            <p className="text-3xl font-bold text-purple-600">${stats.avgPrice.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tier name or creator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTiers.map((tier) => (
          <Card key={tier.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{tier.tierName}</h3>
                  <p className="text-sm text-gray-500">
                    by {tier.creatorName} (@{tier.creatorUrl})
                  </p>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    tier.isPublished
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {tier.isPublished ? 'Published' : 'Draft'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
              <div className="mb-4">
                <p className="text-3xl font-bold text-gray-900">
                  ${tier.amount}
                  <span className="text-sm font-normal text-gray-500">/month</span>
                </p>
                <p className="text-sm text-blue-600 mt-1">{tier.subscriberCount} subscribers</p>
              </div>
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Benefits:</p>
                <ul className="space-y-1">
                  {tier.benefits.map((benefit, idx) => (
                    <li key={idx} className="text-xs text-gray-600 flex items-center">
                      <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button variant="danger" size="sm">
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTiersPage;
